/**
 * SIG Data Service Layer
 * Business logic and data transformation for PROA budget integration
 *
 * Handles transformation between API responses and frontend models,
 * client name matching, and variance calculations.
 */

import type {
  ClientResultApiResponse,
  ClientResult,
  ConsolidatedFinancialData,
  DirectorTarget,
  ClientNameMapping
} from '@/types/financial'

/**
 * Client name mapping for matching PROA targets to actual clients
 * Maps target names from PROA system to actual client names in SIG
 */
export const CLIENT_NAME_MAPPING: ClientNameMapping = {
  'BANCO DO BRASIL PROMO': 'Banco do Brasil',
  'BB SEGUROS + COLIGADAS': 'BB Seguros',
  'BANCO DO BRASIL DIGITAL': 'BB Digital',
  'SEBRAE': 'Sebrae',
  'EMBRATUR': 'Embratur',
  'PETROBRAS': 'Petrobras',
  'FUNCEF': 'Funcef',
  'APEX': 'Apex-Brasil'
}

/**
 * Transform API client result to frontend model
 * Calculates PROA with 10% markup and performance percentage
 *
 * @param apiData - Raw client data from API
 * @returns Transformed client result for UI display
 */
export const transformClientResult = (apiData: ClientResultApiResponse): ClientResult => {
  const proaWithMarkup = apiData.projected * 1.1
  const revenue = apiData.revenue ?? 0
  const performance = Math.min(100, Math.round((revenue / proaWithMarkup) * 100))

  return {
    id: apiData.clientName.toLowerCase().replace(/\s+/g, '-'),
    nome: apiData.clientName,
    valor: revenue,
    proa: proaWithMarkup,
    performance,
    logo: null, // Will be populated from assets
    fallbackIcon: 'Building2',
    color: (apiData.profit ?? 0) > 0 ? 'text-green-500' : 'text-red-500'
  }
}

/**
 * Transform consolidated financial data
 * Passes through the real/proa/variance structure
 * Handles missing PROA data gracefully
 *
 * @param apiData - Consolidated data from API
 * @returns Transformed consolidated data (currently pass-through)
 */
export const transformConsolidatedData = (
  apiData: ConsolidatedFinancialData
): ConsolidatedFinancialData => {
  // Currently pass-through, but provides extension point for future transformations
  return apiData
}

/**
 * Match a client to their corresponding director target
 * Uses CLIENT_NAME_MAPPING for name normalization
 *
 * @param client - Client result object
 * @param targets - Array of director targets
 * @returns Matched target or undefined
 */
export const matchClientToTarget = (
  client: ClientResult,
  targets: DirectorTarget[]
): DirectorTarget | undefined => {
  return targets.find(target => {
    const mappedName = CLIENT_NAME_MAPPING[target.client]
    return mappedName === client.nome
  })
}

/**
 * Calculate variance between actual and PROA
 *
 * @param actual - Actual value
 * @param proa - Budget/target value
 * @returns Variance (positive = over target, negative = under target)
 */
export const calculateVariance = (actual: number, proa: number): number => {
  return actual - proa
}

/**
 * Format variance value for display
 * Includes sign prefix and Brazilian number formatting
 *
 * @param variance - Variance value
 * @returns Formatted string (e.g., "+1.234.567" or "-890.123")
 */
export const formatVariance = (variance: number): string => {
  const sign = variance >= 0 ? '+' : ''
  return `${sign}${variance.toLocaleString('pt-BR')}`
}

/**
 * Get color class for variance display
 * Green for positive variance (over target), red for negative
 *
 * @param variance - Variance value
 * @returns Tailwind color class
 */
export const getVarianceColor = (variance: number): string => {
  return variance >= 0 ? 'text-green-600' : 'text-red-600'
}

/**
 * Format currency for display
 * Uses Brazilian Real (BRL) formatting
 *
 * @param value - Numeric value
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

/**
 * Format percentage for display
 *
 * @param value - Percentage value (0-100)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`
}

/**
 * Check if PROA data is available
 *
 * @param proa - PROA value (can be null)
 * @returns True if PROA data is available
 */
export const hasProaData = (proa: number | null): boolean => {
  return proa !== null && proa !== undefined
}
